import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ArrowLeft, Heart, X, Loader2 } from "lucide-react";
import apiClient from "../../../api/apiClient";
import TopNavBar from "../../../components/TopNavBar";
import tablerBone from "../../../assets/tabler_bone.png";
import { ENDPOINTS } from "../../../api/endPoints";
import { useGlobalAlert } from "../../../context/AlertContext";
import { useNotifications } from "../../../context/NotificationContext";
import userPlaceholder from "../../../assets/userPlaceholder.png";
import appIcon from "../../../assets/appIcon.svg";
import "./TodayWalkNotificationPage.css";
import MobileTopBar from "../../../components/MobileTopBar";

const TodayWalkNotificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUnreadCount } = useNotifications();
  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [treatData, setTreatData] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const notificationId = location.state?.notificationId;

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get(`/notifications/${profile?.id}`);
      if (response.data.status) {
        const data = response.data.data;
        const count = data.filter((n) => !n.is_read).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
    }
  }, [profile?.id]);

  useEffect(() => {
    const fetchTreatDetails = async () => {
      if (!notificationId) return;
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/notifications/get-notification-ById-for-treat/${notificationId}`,
        );
        if (response.data.status && response.data.data.length > 0) {
          setTreatData(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching treat details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTreatDetails();
  }, [notificationId]);

  const isSender = profile?.id === treatData?.sender_user_id;

  const partnerName = isSender
    ? treatData?.receiver_user_name
    : treatData?.sender_user_name;
  const partnerDogName = isSender
    ? treatData?.receiver_dog_name
    : treatData?.sender_dog_name;
  const myDogName = isSender
    ? treatData?.sender_dog_name
    : treatData?.receiver_dog_name;

  const partnerImage = isSender
    ? treatData?.receiver_dog_image?.[0] ||
      treatData?.receiver_profile_image?.[0]
    : treatData?.sender_dog_image?.[0] || treatData?.sender_profile_image?.[0];

  const handleCloseModal = () => {
    setShowLocationModal(false);
  };

  if (loading) {
    return (
      <div className="twn-loading-screen">
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  if (!treatData) {
    return <div className="text-center">No treat data found.</div>;
  }

  return (
    <div className="twn-container">
      {/* DESKTOP HEADER */}
      <div className="twn-header-desktop twn-desktop-only">
        <div
          className="twn-logo-group"
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img src={appIcon} alt="Social Paws" />
          <span className="twn-logo-text">Social Paws</span>
        </div>
        <div className="twn-top-nav-wrapper">
          <TopNavBar theme="dark" />
        </div>
      </div>

      {/* MOBILE HEADER */}
      {/* <div className="twn-header-mobile twn-mobile-only">
        <div className="twn-mobile-top-row">
          <button className="twn-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={22} color="#fff" strokeWidth={3} />
            <span>Back</span>
          </button>
          <div
            className="twn-logo-group"
            onClick={() => navigate("/dashboard-switcher")}
          >
            <img src={appIcon} alt="Social Paws" />
            <span className="twn-logo-text">Social Paws</span>
          </div>
        </div>
        <div className="twn-mobile-nav-row">
          <TopNavBar theme="dark" />
        </div>
      </div> */}
      <div className="notif-ui-navbar-mobile notif-mobile-only">
        <MobileTopBar
          onBack={() => navigate(-1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="notif-mobile-header-bottom">
          <div className="notif-ui-nav-icons">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      {/* CENTER CONTENT */}
      <div className="twn-content-wrapper">
        <div className="twn-panel">
          <div className="twn-card">
            {/* Card Close Button */}
            <button className="twn-card-close-btn" onClick={() => navigate(-1)}>
              <X size={18} strokeWidth={3} color="#A61C1F" />
            </button>

            {/* DYNAMIC TOP TEXT */}
            <div className="twn-top-text">YOU'VE GOT A WALK TODAY!</div>

            <div className="twn-sub-text">
              with {partnerName?.toUpperCase()} at {treatData?.formatted_date}{" "}
              at <br />
              <span className="twn-location-text">
                {treatData?.full_address}
              </span>
            </div>

            {/* IMAGE BLOCK */}
            <div className="twn-image-container">
              <img src={partnerImage || userPlaceholder} alt={partnerName} />
              <div className="twn-image-overlay">
                <div className="twn-overlay-left">
                  <Heart size={22} color="#fff" strokeWidth={2} />
                  <span>{partnerName}</span>
                </div>
                {/* <span className="twn-distance-text">2.5mi away</span> */}
              </div>
            </div>

            {/* DYNAMIC BOTTOM TEXT */}
            <div className="twn-bottom-title">SHARE YOUR LOCATION</div>
            <div className="twn-bottom-desc">
              Sharing your location makes it easier to find each other and helps
              keep interactions safe. You can turn this off anytime.
            </div>

            {/* BUTTONS */}
            <div className="twn-action-btns">
              <button
                className="twn-btn twn-btn-primary"
                onClick={() => setShowLocationModal(true)}
              >
                TURN ON LOCATION SHARING WITH{" "}
                {partnerDogName ? partnerDogName.toUpperCase() : "DOG"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* LOCATION MODAL */}
      {showLocationModal && (
        <div className="twn-modal-overlay">
          <div className="twn-modal-card">
            <button className="twn-modal-close-btn" onClick={handleCloseModal}>
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="twn-modal-content">
              <div className="twn-icons-row">
                <div className="twn-dog-avatar">
                  <img src={partnerImage || userPlaceholder} alt="image" />
                </div>

                <img
                  src={tablerBone}
                  alt="tablerBoneIcon"
                  className="twn-bone-icon"
                />
              </div>
              <h3 className="twn-high-paw">HIGH PAW!</h3>

              <h2 className="twn-modal-text-main">
                You have turned on location-sharing to ensure a successful first
                walk together. We will check back in with you to see how it
                went!
              </h2>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayWalkNotificationPage;
